import React, { useEffect, useRef, useState } from "react";
import { X, RotateCcw, Volume2, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * KanaStrokeModal — stroke-order video viewer for a single kana.
 * Props:
 *  - list: ordered manifest entries for the current syllabary (46 items)
 *  - index: current position in `list`
 *  - onIndex: (newIndex) => void  — navigate within the same series
 *  - onClose: () => void
 *  - speak: (char) => void — reuse existing kana audio
 */
export const KanaStrokeModal = ({ list, index, onIndex, onClose, speak }) => {
  const entry = list[index];
  const videoRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [videoError, setVideoError] = useState(false);

  const hasPrev = index > 0;
  const hasNext = index < list.length - 1;

  // Keyboard: Escape closes, arrows navigate.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasPrev) onIndex(index - 1);
      else if (e.key === "ArrowRight" && hasNext) onIndex(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onIndex, onClose]);

  // New kana: reset error state; the <video key> remount handles the restart.
  useEffect(() => { setVideoError(false); }, [entry?.video]);

  // Apply chosen speed whenever it changes (without index change).
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  if (!entry) return null;

  const traitsLabel = `${entry.traits} trait${entry.traits > 1 ? "s" : ""}`;

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.playbackRate = speed;
    v.play().catch(() => {});
  };

  return (
    <div
      data-testid="kana-stroke-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-[#1A2A40]/70 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4 animate-fadeIn"
    >
      <div
        data-testid="kana-stroke-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full h-full sm:h-auto sm:w-full sm:max-w-[460px] bg-[#FDFBF7] sm:border-2 sm:border-[#1A2A40] sm:rounded-3xl shadow-brut overflow-y-auto flex flex-col"
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="kana-stroke-close"
          aria-label="Fermer"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#1A2A40]/10 hover:bg-[#1A2A40]/20 flex items-center justify-center text-[#1A2A40] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8 flex flex-col gap-5">
          {/* a. Header: kana + romaji + traits */}
          <div className="flex items-center gap-4">
            <span className="font-jp text-5xl sm:text-6xl font-bold text-[#1A2A40] leading-none" data-testid="kana-stroke-char">
              {entry.kana}
            </span>
            <div>
              <div className="font-display font-black text-2xl text-[#E94A3F] lowercase" data-testid="kana-stroke-romaji">{entry.romaji}</div>
              <div className="text-sm text-[#4A5568]" data-testid="kana-stroke-traits">{traitsLabel}</div>
            </div>
          </div>

          {/* b. Video card (already a white square with "Trait X sur Y" baked in) */}
          <div className="relative w-full aspect-square rounded-[24px] overflow-hidden bg-white">
            {videoError ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-[#A0AEC0]" data-testid="kana-stroke-video-error">
                Vidéo indisponible
              </div>
            ) : (
              <video
                key={entry.video}
                ref={videoRef}
                src={entry.video}
                poster={entry.poster}
                muted
                playsInline
                autoPlay
                preload="auto"
                onError={() => setVideoError(true)}
                onLoadedData={(e) => { e.target.playbackRate = speed; }}
                data-testid="kana-stroke-video"
                className="w-full h-full object-contain"
              />
            )}
          </div>

          {/* c. Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={replay}
              data-testid="kana-stroke-replay"
              className="flex-1 bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3 rounded-xl shadow-brut-sm inline-flex items-center justify-center gap-2 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Revoir
            </button>
            <button
              onClick={() => setSpeed((s) => (s === 1 ? 0.5 : 1))}
              data-testid="kana-stroke-speed"
              aria-pressed={speed === 0.5}
              className={`px-4 py-3 rounded-xl font-bold text-sm border-2 transition-colors ${
                speed === 0.5
                  ? "bg-[#1A2A40] text-white border-[#1A2A40]"
                  : "bg-[#FDFBF7] text-[#1A2A40] border-[#1A2A40]/20 hover:border-[#E94A3F]"
              }`}
            >
              Ralentir 0,5×
            </button>
            <button
              onClick={() => speak(entry.kana)}
              data-testid="kana-stroke-speak"
              aria-label={`Écouter ${entry.romaji}`}
              className="w-12 h-12 shrink-0 rounded-xl border-2 border-[#1A2A40]/20 hover:border-[#E94A3F] text-[#1A2A40] flex items-center justify-center transition-colors"
            >
              <Volume2 className="w-5 h-5" />
            </button>
          </div>

          {/* d. Practice hint */}
          <div className="rounded-xl border-2 border-dashed border-[#1A2A40]/15 bg-[#FFF5E0]/60 px-4 py-3 text-sm text-[#1A2A40]">
            À toi : trace-le 3 fois sur papier, dans le même ordre.
          </div>

          {/* e. Prev / position / next */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => hasPrev && onIndex(index - 1)}
              disabled={!hasPrev}
              data-testid="kana-stroke-prev"
              aria-label="Kana précédent"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#1A2A40]/20 font-jp text-lg text-[#1A2A40] hover:border-[#E94A3F] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> {hasPrev ? list[index - 1].kana : ""}
            </button>
            <span className="text-sm font-bold text-[#4A5568]" data-testid="kana-stroke-position">
              {index + 1} / {list.length}
            </span>
            <button
              onClick={() => hasNext && onIndex(index + 1)}
              disabled={!hasNext}
              data-testid="kana-stroke-next"
              aria-label="Kana suivant"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-[#1A2A40]/20 font-jp text-lg text-[#1A2A40] hover:border-[#E94A3F] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              {hasNext ? list[index + 1].kana : ""} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanaStrokeModal;
